<!--
/**
 * FaceRecognition.vue
 * AI-powered face recognition component using face-api.js
 * Loads models from CDN and performs real-time face detection + verification
 *
 * Events:
 *   @verified(confidence)  — wajah dikenali, confidence = 0-1
 *   @failed                — wajah tidak dikenali
 *   @no-descriptor         — karyawan belum enroll wajah
 *   @camera-error(error)   — kamera tidak bisa diakses
 *
 * Props:
 *   empNumber (required)   — employee number for descriptor lookup
 *   mode: 'verify' | 'enroll'
 */
-->
<template>
  <div class="face-recognition-wrapper">
    <!-- Status Banner -->
    <transition name="face-status-fade">
      <div
        v-if="status"
        :class="['face-status-banner', `face-status-banner--${statusType}`]"
      >
        <div class="face-status-icon">{{ statusIcon }}</div>
        <div class="face-status-text">{{ status }}</div>
      </div>
    </transition>

    <!-- Camera Container -->
    <div
      class="face-camera-wrapper"
      :class="{
        'face-camera-wrapper--verified': isVerified,
        'face-camera-wrapper--failed': isFailed,
      }"
    >
      <!-- Loading overlay -->
      <div v-if="isLoadingModels" class="face-loading-overlay">
        <div class="face-loading-spinner"></div>
        <div class="face-loading-text">
          Memuat AI Model... {{ loadingProgress }}%
        </div>
      </div>

      <!-- Camera feed -->
      <video
        ref="videoRef"
        class="face-video"
        autoplay
        playsinline
        muted
      ></video>

      <!-- Canvas for face-api.js detection overlays -->
      <canvas ref="canvasRef" class="face-canvas"></canvas>

      <!-- Face outline guide -->
      <div class="face-guide-overlay">
        <svg viewBox="0 0 300 300" class="face-guide-svg">
          <ellipse
            cx="150"
            cy="150"
            rx="100"
            ry="130"
            fill="none"
            :stroke="guideColor"
            stroke-width="3"
            stroke-dasharray="10,5"
            class="face-guide-ellipse"
          />
        </svg>
      </div>

      <!-- Scan animation when verifying -->
      <div v-if="isScanning" class="face-scan-line"></div>

      <!-- Verified overlay -->
      <transition name="verified-overlay">
        <div v-if="isVerified" class="face-verified-overlay">
          <div class="face-verified-checkmark">✓</div>
          <div class="face-verified-text">Wajah Terverifikasi</div>
          <div class="face-confidence-bar">
            <div
              class="face-confidence-fill"
              :style="{width: `${lastConfidence * 100}%`}"
            ></div>
          </div>
          <div class="face-confidence-label">
            Akurasi: {{ Math.round(lastConfidence * 100) }}%
          </div>
        </div>
      </transition>
    </div>

    <!-- Mode: Enroll — capture button -->
    <div v-if="mode === 'enroll'" class="face-enroll-controls">
      <div class="face-enroll-hint">
        <span class="face-hint-dot"></span>
        Posisikan wajah di dalam lingkaran, lalu klik tombol di bawah
      </div>
      <button
        class="face-capture-btn"
        :disabled="isLoadingModels || isCapturing || !isCameraReady"
        @click="enrollFace"
      >
        <span v-if="isCapturing" class="face-btn-spinner"></span>
        {{ isCapturing ? 'Memproses...' : '📸 Ambil & Daftarkan Wajah' }}
      </button>
    </div>

    <!-- Debug info (dev only) -->
    <div v-if="showDebug" class="face-debug">
      <small
        >Models: {{ modelsLoaded }} | Camera: {{ isCameraReady }} | Descriptors:
        {{ descriptorLoaded }}</small
      >
    </div>
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';

const FACEAPI_CDN =
  'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';
const MODELS_CDN_BASE =
  'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@latest/model';

export default {
  name: 'FaceRecognition',

  props: {
    empNumber: {
      type: Number,
      required: true,
    },
    mode: {
      type: String,
      default: 'verify', // 'verify' | 'enroll'
      validator: (v) => ['verify', 'enroll'].includes(v),
    },
    showDebug: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['verified', 'failed', 'no-descriptor', 'camera-error', 'enrolled'],

  data() {
    return {
      isLoadingModels: true,
      modelsLoaded: false,
      isCameraReady: false,
      isScanning: false,
      isVerified: false,
      isFailed: false,
      isCapturing: false,
      lastConfidence: 0,
      descriptorLoaded: false,
      storedDescriptor: null,
      status: 'Memuat model AI...',
      statusType: 'loading', // 'loading' | 'success' | 'error' | 'warning'
      loadingProgress: 0,
      stream: null,
      detectionInterval: null,
      verificationAttempts: 0,
      MAX_ATTEMPTS: 5,
      CONFIDENCE_THRESHOLD: 0.55, // Cocok jika jarak Euclidean < threshold
    };
  },

  computed: {
    statusIcon() {
      const icons = {loading: '⟳', success: '✓', error: '✗', warning: '⚠'};
      return icons[this.statusType] || '•';
    },
    guideColor() {
      if (this.isVerified) return '#4caf50';
      if (this.isFailed) return '#f44336';
      return 'rgba(255,255,255,0.8)';
    },
  },

  async mounted() {
    await this.initialize();
  },

  beforeUnmount() {
    this.stopCamera();
    if (this.detectionInterval) clearInterval(this.detectionInterval);
  },

  methods: {
    async initialize() {
      try {
        // Step 1: Load face-api.js from CDN
        await this.loadFaceApiScript();
        this.loadingProgress = 40;

        // Step 2: Load AI models
        await this.loadModels();
        this.loadingProgress = 80;
        this.modelsLoaded = true;

        // Step 3: Start camera
        await this.startCamera();
        this.loadingProgress = 100;
        this.isLoadingModels = false;

        // Step 4: Load stored descriptor (verify mode only)
        if (this.mode === 'verify') {
          await this.loadStoredDescriptor();
          if (this.descriptorLoaded) {
            this.setStatus('Siap. Posisikan wajah Anda...', 'loading');
            this.startFaceVerification();
          } else {
            this.setStatus('Wajah belum terdaftar. Hubungi admin.', 'warning');
            this.$emit('no-descriptor');
          }
        } else {
          this.setStatus('Siap untuk mendaftarkan wajah.', 'success');
        }
      } catch (err) {
        this.isLoadingModels = false;
        this.setStatus('Gagal memuat: ' + err.message, 'error');
        console.error('[FaceRecognition] Init error:', err);
      }
    },

    loadFaceApiScript() {
      return new Promise((resolve, reject) => {
        if (window.faceapi) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = FACEAPI_CDN;
        script.onload = resolve;
        script.onerror = () =>
          reject(
            new Error(
              'Gagal memuat face-api.js dari CDN. Periksa koneksi internet.',
            ),
          );
        document.head.appendChild(script);
      });
    },

    async loadModels() {
      const faceapi = window.faceapi;
      this.setStatus('Memuat model pendeteksi wajah...', 'loading');

      await Promise.all([
        faceapi.nets.ssdMobilenetv1.loadFromUri(MODELS_CDN_BASE),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_CDN_BASE),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_CDN_BASE),
      ]);
    },

    async startCamera() {
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {facingMode: 'user', width: 640, height: 480},
          audio: false,
        });
        const video = this.$refs.videoRef;
        video.srcObject = this.stream;
        await new Promise((resolve) => {
          video.onloadedmetadata = resolve;
        });
        this.isCameraReady = true;
      } catch (err) {
        this.$emit('camera-error', err);
        throw new Error('Tidak bisa mengakses kamera: ' + err.message);
      }
    },

    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }
    },

    async loadStoredDescriptor() {
      try {
        const http = new APIService(
          window.appGlobal.baseUrl,
          '/api/v2/attendance/face-descriptors',
        );
        const res = await http.getById(this.empNumber);
        const {enrolled, descriptor} = res.data.data;
        if (enrolled && descriptor) {
          this.storedDescriptor = new Float32Array(descriptor);
          this.descriptorLoaded = true;
        }
      } catch {
        this.descriptorLoaded = false;
      }
    },

    startFaceVerification() {
      this.isScanning = true;
      this.verificationAttempts = 0;

      // Run detection every 800ms
      this.detectionInterval = setInterval(async () => {
        if (!this.isCameraReady || !this.modelsLoaded || !this.storedDescriptor)
          return;
        if (this.isVerified) {
          clearInterval(this.detectionInterval);
          return;
        }
        await this.detectAndVerify();
      }, 800);
    },

    async detectAndVerify() {
      const faceapi = window.faceapi;
      const video = this.$refs.videoRef;
      const canvas = this.$refs.canvasRef;

      if (!video || !canvas) return;

      // Match canvas size to video
      const displaySize = {width: video.videoWidth, height: video.videoHeight};
      faceapi.matchDimensions(canvas, displaySize);

      // Detect face with landmarks and descriptor
      const detection = await faceapi
        .detectSingleFace(
          video,
          new faceapi.SsdMobilenetv1Options({minConfidence: 0.5}),
        )
        .withFaceLandmarks()
        .withFaceDescriptor();

      // Clear canvas
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!detection) {
        this.verificationAttempts++;
        if (this.verificationAttempts === 1) {
          this.setStatus('Wajah tidak terdeteksi...', 'warning');
        }
        return;
      }

      // Draw detection box
      const resizedDetections = faceapi.resizeResults(detection, displaySize);
      faceapi.draw.drawDetections(canvas, [resizedDetections]);
      faceapi.draw.drawFaceLandmarks(canvas, [resizedDetections]);

      // Compare descriptor with stored one
      const distance = faceapi.euclideanDistance(
        detection.descriptor,
        this.storedDescriptor,
      );
      const confidence = Math.max(0, 1 - distance);

      this.setStatus(
        `Verifikasi... (${Math.round(confidence * 100)}%)`,
        'loading',
      );

      if (distance < this.CONFIDENCE_THRESHOLD) {
        // Face verified!
        clearInterval(this.detectionInterval);
        this.isScanning = false;
        this.isVerified = true;
        this.isFailed = false;
        this.lastConfidence = confidence;
        this.setStatus('Wajah terverifikasi!', 'success');
        this.$emit('verified', confidence);
      } else {
        this.verificationAttempts++;
        if (this.verificationAttempts >= this.MAX_ATTEMPTS) {
          // Too many failures
          clearInterval(this.detectionInterval);
          this.isScanning = false;
          this.isFailed = true;
          this.setStatus('Wajah tidak dikenali. Absensi ditolak.', 'error');
          this.$emit('failed');
        }
      }
    },

    /**
     * Enroll mode: capture current face and save descriptor to DB
     */
    async enrollFace() {
      this.isCapturing = true;
      this.setStatus('Mendeteksi wajah...', 'loading');
      const faceapi = window.faceapi;
      const video = this.$refs.videoRef;

      try {
        const detection = await faceapi
          .detectSingleFace(
            video,
            new faceapi.SsdMobilenetv1Options({minConfidence: 0.5}),
          )
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (!detection) {
          this.setStatus('Wajah tidak terdeteksi. Coba lagi.', 'error');
          this.isCapturing = false;
          return;
        }

        const descriptor = Array.from(detection.descriptor);
        const http = new APIService(
          window.appGlobal.baseUrl,
          '/api/v2/attendance/face-descriptors',
        );

        await http.create({
          empNumber: this.empNumber,
          descriptor: JSON.stringify(descriptor),
        });

        this.setStatus('Wajah berhasil didaftarkan!', 'success');
        this.isVerified = true;
        this.$emit('enrolled');
      } catch (err) {
        this.setStatus('Gagal mendaftarkan: ' + err.message, 'error');
        console.error('[FaceRecognition] Enroll error:', err);
      } finally {
        this.isCapturing = false;
      }
    },

    /**
     * Capture photo from video for attendance record
     */
    capturePhoto() {
      const video = this.$refs.videoRef;
      if (!video || video.videoWidth === 0) return null;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      return canvas.toDataURL('image/jpeg', 0.8);
    },

    setStatus(text, type = 'loading') {
      this.status = text;
      this.statusType = type;
    },
  },
};
</script>

<style lang="scss" scoped>
.face-recognition-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
}

// ─── Status Banner ────────────────────────────────────────────────────────────
.face-status-banner {
  width: 100%;
  padding: 10px 16px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;

  &--loading {
    background: rgba(33, 150, 243, 0.15);
    color: #1565c0;
    border: 1px solid rgba(33, 150, 243, 0.3);
  }
  &--success {
    background: rgba(76, 175, 80, 0.15);
    color: #2e7d32;
    border: 1px solid rgba(76, 175, 80, 0.3);
  }
  &--error {
    background: rgba(244, 67, 54, 0.15);
    color: #c62828;
    border: 1px solid rgba(244, 67, 54, 0.3);
  }
  &--warning {
    background: rgba(255, 152, 0, 0.15);
    color: #e65100;
    border: 1px solid rgba(255, 152, 0, 0.3);
  }
}

.face-status-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

// ─── Camera ──────────────────────────────────────────────────────────────────
.face-camera-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 4/3;
  border-radius: 16px;
  overflow: hidden;
  background: #111;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 3px solid transparent;
  transition: border-color 0.4s ease, box-shadow 0.4s ease;

  &--verified {
    border-color: #4caf50;
    box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.25), 0 8px 32px rgba(0, 0, 0, 0.3);
  }

  &--failed {
    border-color: #f44336;
    box-shadow: 0 0 0 4px rgba(244, 67, 54, 0.25), 0 8px 32px rgba(0, 0, 0, 0.3);
    animation: shake 0.4s ease;
  }
}

.face-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1); // Mirror effect
  display: block;
}

.face-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transform: scaleX(-1);
}

// ─── Scan Line ────────────────────────────────────────────────────────────────
.face-scan-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, transparent, #42a5f5, transparent);
  animation: scanMove 2s linear infinite;
  box-shadow: 0 0 12px 2px rgba(66, 165, 245, 0.6);
}

// ─── Guide Overlay ────────────────────────────────────────────────────────────
.face-guide-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.face-guide-svg {
  width: 60%;
  height: 60%;
}

.face-guide-ellipse {
  transition: stroke 0.4s ease;
  animation: pulse 2s ease-in-out infinite;
}

// ─── Loading Overlay ──────────────────────────────────────────────────────────
.face-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  z-index: 10;
}

.face-loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top-color: #42a5f5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.face-loading-text {
  color: white;
  font-size: 13px;
  font-weight: 500;
}

// ─── Verified Overlay ─────────────────────────────────────────────────────────
.face-verified-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 5;
}

.face-verified-checkmark {
  width: 72px;
  height: 72px;
  background: #4caf50;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  color: white;
  animation: popIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 0 0 8px rgba(76, 175, 80, 0.3);
}

.face-verified-text {
  color: white;
  font-size: 16px;
  font-weight: 700;
}

.face-confidence-bar {
  width: 160px;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
}

.face-confidence-fill {
  height: 100%;
  background: linear-gradient(90deg, #81c784, #4caf50);
  border-radius: 3px;
  transition: width 0.6s ease;
}

.face-confidence-label {
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
}

// ─── Enroll Controls ──────────────────────────────────────────────────────────
.face-enroll-controls {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.face-enroll-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #546e7a;
}

.face-hint-dot {
  width: 8px;
  height: 8px;
  background: #42a5f5;
  border-radius: 50%;
  flex-shrink: 0;
  animation: pulse 1.5s ease-in-out infinite;
}

.face-capture-btn {
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, #1565c0, #42a5f5);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.2s, transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.face-btn-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

// ─── Debug ───────────────────────────────────────────────────────────────────
.face-debug {
  color: #90a4ae;
  font-size: 11px;
}

// ─── Animations ──────────────────────────────────────────────────────────────
@keyframes scanMove {
  0% {
    top: 0;
  }
  100% {
    top: 100%;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes popIn {
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-8px);
  }
  75% {
    transform: translateX(8px);
  }
}

// ─── Transitions ─────────────────────────────────────────────────────────────
.face-status-fade-enter-active,
.face-status-fade-leave-active {
  transition: all 0.3s ease;
}
.face-status-fade-enter-from,
.face-status-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.verified-overlay-enter-active {
  transition: all 0.4s ease;
}
.verified-overlay-enter-from {
  opacity: 0;
}
</style>
